/**
 * Studio — the brief → explore → finalize flow.
 *
 * Behind the `design-studio` flag, bound to the real Editor. A persistent
 * project-name top bar over full-screen, button-driven phase swaps: Brief →
 * Explore → Map → Finalize → Report.
 */
import { observer } from "mobx-react-lite";
import { runInAction } from "mobx";
import { useState, useEffect, useRef, Fragment } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { Editor } from "src/types/Editor";
import { World } from "src/types/World";
import { ZoneType } from "src/types/Zone";
import type { GeneratedItem } from "src/types/GeneratedItem";
import { ExportViewScreen } from "./ExportViewScreen";
import { useApi } from "src/contexts/api.context";
import { useActiveParams } from "src/hooks/useActiveParams";
import { useActiveProjectQuery } from "src/hooks/useActiveProjectQuery";
import { useGetUser } from "src/hooks/useGetUser";
import tessaLogo from "src/assets/tessa-logo.svg";
import { Icon, ACCENT } from "./Icon";
import { BriefStep } from "./BriefStep";
import { ExploreView, deriveRows } from "./ExploreView";
import { DesignMapScreen, type MapDesignRef } from "./DesignMapScreen";
import { FinalizeStep } from "./FinalizeStep";
import { BoardState } from "./BoardState";
import {
  makeBriefSection,
  cloneBrief,
  UNIT_MIX,
  type BriefSectionState,
  type BoardBrief,
  type BriefZone,
  type Overlay,
} from "./briefModel";

export type Step = "brief" | "explore" | "finalize";

/** Designs that must be locked in before Finalize is reachable. */
export const NEEDED = 3;

/** Default map centre for a new brief — the v3 flow is German-first. */
const HAMBURG = { longitude: 9.9937, latitude: 53.5511 };

export const StudioApp = observer(function StudioApp({ editor }: { editor: Editor }) {
  // editor is bound further in later chunks (generation, persistence); for now
  // it seeds the project name.
  const [step, setStep] = useState<Step>("explore");
  const [sections, setSections] = useState<BriefSectionState[]>(() => []);
  const [briefSectionId, setBriefSectionId] = useState<string | null>(null);
  const [isNewBrief, setIsNewBrief] = useState(true);
  const [mapDesign, setMapDesign] = useState<MapDesignRef | null>(null);
  const [mapMode3d, setMapMode3d] = useState(true);
  const [exportTarget, setExportTarget] = useState<{
    items: GeneratedItem[];
    reportType: "construction" | "financial";
  } | null>(null);
  const projectQuery = useActiveProjectQuery();
  // Project name precedence: a name set this session (kept on the world, the
  // editor god-object, so it survives step changes + remounts) wins; otherwise
  // the project's persisted name. The scenario world file carries no name —
  // Api.GetScenarioWithWorldFile returns label:"" — so the real name comes from
  // the Project resource via useActiveProjectQuery. `nameAuto` keeps the name
  // tracking the searched address until the user renames manually.
  const projectName = editor.world?.label?.trim() || projectQuery.data?.name?.trim() || "Untitled";
  const [nameAuto] = useState(() => !editor.world?.label);
  const [board] = useState(() => new BoardState(editor));
  useEffect(() => () => board.destroy(), [board]);

  // A paid report link redirects back to Studio (via /checkout/return) with
  // ?paidReport=<type>. Once the project's shown designs have loaded, open the
  // export view over all of them and clear the flag so a refresh doesn't retrigger.
  const [searchParams, setSearchParams] = useSearchParams();
  const paidReport = searchParams.get("paidReport");
  const paidDeliveredRef = useRef(false);
  useEffect(() => {
    if (paidDeliveredRef.current) return;
    if (paidReport !== "construction" && paidReport !== "financial") return;
    const items = sections.flatMap((s) => deriveRows(board.shownOf(s.id))).map((d) => d.item);
    if (items.length === 0) return; // designs still loading
    paidDeliveredRef.current = true;
    setExportTarget({ items, reportType: paidReport });
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("paidReport");
        return next;
      },
      { replace: true }
    );
  }, [paidReport, sections, board, setSearchParams]);

  const { projectId } = useActiveParams();
  const api = useApi();
  // Share the canvas's scenario cache so we don't double-fetch.
  const scenarioQuery = useQuery({
    queryKey: [projectId, "scenario"],
    queryFn: async () => {
      if (!projectId) return null;
      const scenario = await api.GetScenarioWithWorldFile(projectId);
      if (!scenario) return null;
      return editor.loader.loadFromScenarioWithDesign(scenario);
    },
    enabled: !!projectId,
    staleTime: Infinity,
  });

  // Load the world for the map, and for an existing project seed Revision 1 and
  // generate its pool. A new project loads a blank Hamburg world and stays on
  // the empty Explore homepage until the user creates a brief.
  const seededRef = useRef(false);
  useEffect(() => {
    if (!scenarioQuery.isSuccess) {
      console.log("[NAV-DIAG] effect: isSuccess=false, status=", scenarioQuery.status);
      return;
    }
    void (async () => {
      console.log("[NAV-DIAG] effect run:", {
        editorWorld: !!editor.world,
        editorHasZone: editor.world?.hasZone,
        dataHasZone: scenarioQuery.data?.hasZone,
        listLen: editor.designGenerator.list.length,
        isRunning: editor.designGenerator.isRunning,
        seeded: seededRef.current,
        totalDesigns: board.totalDesigns,
      });
      if (!editor.world) {
        // A freshly-created project still has a scenario, but with an EMPTY
        // world (no zones) — only seed Revision 1 when there's real content.
        const scenarioWorld = scenarioQuery.data;
        const existing = scenarioWorld?.hasZone ?? false;
        if (existing && !seededRef.current) {
          seededRef.current = true;
          const r1 = makeBriefSection(1);
          setSections([r1]);
          board.prepareLive(r1.id);
        }
        const world =
          existing && scenarioWorld
            ? scenarioWorld
            : new World({ longitude: HAMBURG.longitude, latitude: HAMBURG.latitude });
        await editor.loadFromWorld(world);
        editor.mapState.setWorld(editor.world);
        if (existing) board.commitIfIdle();
      } else if (editor.world.hasZone && !seededRef.current) {
        // World already loaded — e.g. navigating in from the canvas, where the
        // shared editor kept its world and generated designs. Seed revision 1,
        // then SHOW the designs it already has (without regenerating); only
        // generate if there are none yet. Without this, arriving via the nav link
        // (editor.world already set) left the board empty until a full refresh.
        const hasDesigns = editor.designGenerator.list.length > 0;
        if (hasDesigns || !editor.designGenerator.isRunning) {
          seededRef.current = true;
          const r1 = makeBriefSection(1);
          setSections([r1]);
          if (hasDesigns) {
            board.prepareLive(r1.id);
            board.commitIfIdle(r1.id);
          } else {
            board.generateInto(r1.id, editor.world);
          }
        }
      }
    })();
    // Seed the brief once the scenario has loaded. Intentionally NOT keyed on
    // scenarioQuery.data: the canvas autosave writes editor.world back into this
    // SAME query ([projectId,"scenario"]) via setQueryData on every save. Re-running
    // this effect on that write calls setSections and remounts the brief map
    // mid-edit — disposing its WebGL contexts and eventually crashing the map.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board, editor, scenarioQuery.isSuccess]);

  const briefSection = sections.find((s) => s.id === briefSectionId) ?? null;

  const updSection = (id: string, fn: (s: BriefSectionState) => BriefSectionState) =>
    setSections((ss) => ss.map((s) => (s.id === id ? fn(s) : s)));
  const setBriefFor = (id: string, brief: BoardBrief) => updSection(id, (s) => ({ ...s, brief }));
  const setZonesFor = (id: string, zones: BriefZone[]) => updSection(id, (s) => ({ ...s, zones }));
  const setOverlaysFor = (id: string, overlays: Overlay[]) =>
    updSection(id, (s) => ({ ...s, overlays }));
  const setAddressFor = (id: string, address: string) => {
    updSection(id, (s) => ({ ...s, address }));
    // For a still-unnamed project, derive a placeholder name from the searched
    // location. Never override a name the project already has (session label or
    // its persisted project name).
    if (nameAuto && address && !editor.world?.label && !projectQuery.data?.name?.trim()) {
      const area = address.split(",")[0].trim();
      runInAction(() => editor.world?.setLabel(`${area} · Jun 2026`));
    }
  };
  // Apply a brief onto the real buildable zones (massing, height, mix, podium).
  const applyBrief = (brief: BoardBrief) => {
    const world = editor.world;
    if (!world) return;
    runInAction(() => {
      world.brief.setBuildingSeparation(brief.separation);
      world.zones
        .filter((z) => z.zoneType === ZoneType.buildable)
        .forEach((z) => {
          z.brief.setResidentialFloorCount(brief.floors);
          z.brief.buildingHeight = { minimum: brief.minHeight, maximum: brief.maxHeight };

          z.brief.unitMix.categories.forEach((cat) => {
            const idx = UNIT_MIX.findIndex((u) => u.code === cat.label);
            if (idx < 0) return;
            const m = brief.mix[idx];
            cat.enabled = m.on;
            cat.locked = m.lock;
            cat.value = m.on ? m.pct / 100 : 0;
          });

          const wantPodium = brief.podium.on;
          if (wantPodium && !z.brief.podium) z.brief.togglePodium();
          if (!wantPodium && z.brief.podium) z.brief.togglePodium();
          if (z.brief.podium) {
            z.brief.podium.setFloorCount(brief.podium.floors);
            z.brief.podium.setHeight(brief.podium.height);
          }
        });
    });
  };

  // First generation of a brand-new revision.
  const continueFromBrief = () => {
    if (!briefSection || editor.designGenerator.isRunning) return;
    applyBrief(briefSection.brief);
    board.generateInto(briefSection.id, editor.world);
    updSection(briefSection.id, (s) => ({ ...s, appliedBrief: JSON.stringify(s.brief) }));
    setBriefSectionId(null);
    setStep("explore");
  };

  // Copy a revision's brief (with optional per-zone floor overrides) into a NEW
  // revision and generate it, leaving the source untouched. Existing revisions
  // are immutable — editing only ever forks.
  const forkRevision = (
    source: BriefSectionState,
    brief: BoardBrief,
    floorOverrides?: Record<string, number>
  ) => {
    if (!editor.world) return;
    const copy: BriefSectionState = {
      ...makeBriefSection(sections.length + 1),
      brief: cloneBrief(brief),
      appliedBrief: JSON.stringify(brief),
      address: source.address,
      zones: source.zones,
    };
    setSections((ss) =>
      ss
        .map((s) => (s.id === source.id ? { ...s, brief: JSON.parse(s.appliedBrief) } : s))
        .concat(copy)
    );
    applyBrief(copy.brief);
    if (floorOverrides) {
      runInAction(() => {
        Object.entries(floorOverrides).forEach(([zoneId, floors]) => {
          editor.world?.zones.find((z) => z.id === zoneId)?.brief.setResidentialFloorCount(floors);
        });
      });
    }
    board.generateInto(copy.id, editor.world);
  };

  const createBrief = () => {
    const s = makeBriefSection(sections.length + 1);
    setSections((ss) => [...ss, s]);
    setBriefSectionId(s.id);
    setIsNewBrief(true);
    setStep("brief");
  };
  // Editing forks UP FRONT — a copy of the source becomes a new draft revision
  // and that's what you edit (zones included). The source's generated designs
  // stay frozen; the draft becomes a real revision when you generate it. So you
  // never silently edit an existing revision: it's a new one from the first edit.
  const editBrief = (source: BriefSectionState) => {
    const draft: BriefSectionState = {
      ...makeBriefSection(sections.length + 1),
      brief: cloneBrief(source.brief),
      appliedBrief: "",
      address: source.address,
      zones: source.zones,
    };
    setSections((ss) => [...ss, draft]);
    setBriefSectionId(draft.id);
    setIsNewBrief(false);
    setStep("brief");
  };
  // The brief is already its own draft revision — generating commits it.
  const saveAsNewRevision = continueFromBrief;
  // Leaving the brief drops the un-generated draft (every brief is a new draft
  // revision now — editing never touches an existing one).
  const cancelBrief = () => {
    const id = briefSectionId;
    if (id && board.shownOf(id).length === 0) {
      setSections((ss) => ss.filter((s) => s.id !== id));
    }
    setBriefSectionId(null);
    setStep("explore");
  };

  // Inspector floor edit → a new revision with those per-zone floors.
  const regenerateSection = (sectionId: string, floorOverrides: Record<string, number>) => {
    const source = sections.find((s) => s.id === sectionId);
    if (source) forkRevision(source, source.brief, floorOverrides);
  };

  const pending = briefSection
    ? JSON.stringify(briefSection.brief) !== briefSection.appliedBrief
    : false;

  // Map view: keep narrows down by removing; Finalize unlocks at ≤ NEEDED left.
  const removeMapDesign = (sectionId: string, key: string) => {
    const item = board.shownOf(sectionId).find((it) => it.rgWorldHashKey === key);
    if (item) board.remove(sectionId, item);
  };
  const finalizeFromMap = () => {
    setMapDesign(null);
    setStep("finalize");
  };

  return (
    <>
      <div className="w-screen h-screen flex flex-col bg-[#f9fafb] text-[#1f2937] overflow-hidden">
        <ProjectTopBar
          projectName={projectName}
          designCount={board.totalDesigns}
          needed={NEEDED}
          onFinalize={() => setStep("finalize")}
        />
        <div className="flex-1 min-h-0 overflow-hidden">
          {step === "brief" && briefSection && (
            <BriefStep
              editor={editor}
              section={briefSection}
              setBriefFor={setBriefFor}
              setAddressFor={setAddressFor}
              setZonesFor={setZonesFor}
              setOverlaysFor={setOverlaysFor}
              onContinue={continueFromBrief}
              isNew={isNewBrief}
              generating={editor.designGenerator.isRunning}
              onSaveAsNew={saveAsNewRevision}
              pending={pending}
              canBack
              onExit={cancelBrief}
            />
          )}
          {step === "explore" && (
            <ExploreView
              board={board}
              sections={sections}
              projectName={projectName}
              onCreateBrief={createBrief}
              onEditBrief={editBrief}
              onOpenMap={(ref) => setMapDesign(ref)}
              onFinalize={() => setStep("finalize")}
              needed={NEEDED}
            />
          )}
          {step === "finalize" && (
            <FinalizeStep
              board={board}
              sections={sections}
              projectName={projectName}
              onBack={() => setStep("explore")}
              onSetExportView={(items, reportType) => setExportTarget({ items, reportType })}
            />
          )}
        </div>
      </div>
      {mapDesign && (
        <DesignMapScreen
          editor={editor}
          board={board}
          sections={sections}
          active={mapDesign}
          setActive={setMapDesign}
          mode3d={mapMode3d}
          setMode3d={setMapMode3d}
          onRemove={removeMapDesign}
          onFinalize={finalizeFromMap}
          canFinalize={board.totalDesigns >= 1 && board.totalDesigns <= NEEDED}
          onClose={() => setMapDesign(null)}
          onEditBrief={(section) => {
            setMapDesign(null);
            editBrief(section);
          }}
          onRegenerateSection={regenerateSection}
        />
      )}
      {exportTarget && projectId && (
        <ExportViewScreen
          editor={editor}
          items={exportTarget.items}
          projectId={projectId}
          projectName={projectName}
          reportType={exportTarget.reportType}
          onClose={() => setExportTarget(null)}
        />
      )}
    </>
  );
});

/** Top bar from the v3 wireframe: project name (rename lives on the projects page) + Share + avatar. */
function ProjectTopBar({
  projectName,
  designCount,
  needed,
  onFinalize,
}: {
  projectName: string;
  designCount: number;
  needed: number;
  onFinalize: () => void;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const caret = <path d="M6 9l6 6 6-6" />;
  const canFinalize = designCount >= 1 && designCount <= needed;
  const user = useGetUser().data;
  const initials =
    (user
      ? (user.profile.firstName?.[0] ?? "") + (user.profile.lastName?.[0] ?? "") ||
        (user.email?.[0] ?? "")
      : ""
    ).toUpperCase() || "·";

  return (
    <div className="relative h-12 shrink-0 border-b border-[#d1d5db] bg-white flex items-center px-3 gap-3">
      <button
        onClick={() => navigate("/home")}
        title="Back to projects"
        className="shrink-0 relative -top-px rounded-sm hover:opacity-70"
      >
        <img src={tessaLogo} alt="Tessa" className="h-5" />
      </button>
      <div className="w-px h-5 bg-[#e5e7eb] shrink-0" />
      <div className="relative flex items-center">
        <span className="px-2 py-1 text-sm font-semibold text-[#374151] max-w-[240px] truncate">
          {projectName}
        </span>
        <button
          onClick={() => setOpen(!open)}
          className="px-1 py-1.5 rounded-sm hover:bg-[#f3f4f6] text-[#9ca3af]"
        >
          <Icon d={caret} size={14} />
        </button>
        {open && (
          <Fragment>
            <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
            <div className="absolute left-0 top-full mt-1 z-40 w-52 bg-white border border-[#e5e7eb] rounded-md shadow-lg ring-1 ring-black/5 p-1.5">
              <button
                onClick={() => {
                  setOpen(false);
                  navigate("/home");
                }}
                className="w-full text-left px-2 py-1.5 rounded-sm hover:bg-[#f3f4f6] text-[12px] text-[#374151]"
              >
                New project…
              </button>
            </div>
          </Fragment>
        )}
      </div>
      <div className="grow" />
      <button
        onClick={() => setShareOpen(true)}
        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-sm border border-[#d1d5db] text-[12px] text-[#4b5563] hover:bg-[#f9fafb]"
      >
        <Icon
          d={
            <g>
              <circle cx="6" cy="12" r="2.5" />
              <circle cx="18" cy="6" r="2.5" />
              <circle cx="18" cy="18" r="2.5" />
              <path d="M8.2 10.8l7.6-3.6M8.2 13.2l7.6 3.6" />
            </g>
          }
          size={15}
        />{" "}
        Share
      </button>
      <span
        title={user?.email ?? undefined}
        className="w-8 h-8 rounded-full border border-[#d1d5db] bg-[#f3f4f6] grid place-items-center text-[11px] font-medium text-[#6b7280]"
      >
        {initials}
      </span>

      {shareOpen && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/30 p-4"
          onClick={() => setShareOpen(false)}
        >
          <div
            className="w-[400px] max-w-full rounded-xl border border-[#e5e7eb] bg-white shadow-2xl p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-3">
              <span
                className="w-9 h-9 shrink-0 grid place-items-center rounded-full text-white"
                style={{ background: canFinalize ? "#16a34a" : ACCENT }}
              >
                <Icon d={<path d="M5 13l4 4 10-10" />} size={18} />
              </span>
              <div>
                <h3 className="text-[15px] font-semibold text-[#111827]">
                  {canFinalize ? "Ready to share" : `Pick your top ${needed} designs`}
                </h3>
                <p className="text-[12px] text-[#6b7280] mt-0.5 leading-snug">
                  {canFinalize
                    ? `You're down to ${designCount} design${designCount === 1 ? "" : "s"}. Finalize to generate shareable reports.`
                    : `Sharing and reports work on a shortlist. You have ${designCount} designs — narrow down to ${needed} or fewer, then finalize.`}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setShareOpen(false)}
                className="h-9 px-3 rounded-lg text-[12px] font-medium text-[#6b7280] hover:bg-[#f3f4f6]"
              >
                {canFinalize ? "Not yet" : "Keep exploring"}
              </button>
              <button
                onClick={() => {
                  setShareOpen(false);
                  if (canFinalize) onFinalize();
                }}
                disabled={!canFinalize}
                className={
                  "h-9 px-4 rounded-lg text-[12px] font-semibold inline-flex items-center gap-1.5 " +
                  (canFinalize ? "text-white" : "bg-[#f3f4f6] text-[#9ca3af] cursor-not-allowed")
                }
                style={canFinalize ? { background: "#16a34a" } : undefined}
              >
                {canFinalize ? "Finalize now" : `${designCount - needed} more to remove`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
